// src/features/wf2/popup/W2EditarJustificativa.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./W2EditarModal.css";
import "./W2EditarJustificativa.css";
import { listDesviosEditarOpcoes } from "../api";

function CritDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) => String(opt ?? "").trim())
        .filter(Boolean),
    [options]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      setMenuStyle(null);
      return undefined;
    }

    const updateDirection = () => {
      const menuGap = 2;
      const anchorEl = triggerRef.current || rootRef.current;
      if (!anchorEl) return;

      const rect = anchorEl.getBoundingClientRect();
      const scrollBoundary =
        anchorEl.closest(".ed-crit__content") || anchorEl.closest(".ed-crit__card");
      const boundaryRect = scrollBoundary?.getBoundingClientRect?.() || null;
      const desiredMenuHeight = Math.min(Math.max(safeOptions.length * 40, 56), 220) + 8;
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const availableSpace = Math.min(
        220,
        Math.max(140, Math.floor(spaceBelow - menuGap))
      );
      const top = Math.round(rect.bottom + menuGap);

      setOpenUp(false);
      setMenuStyle({
        top: `${top}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        maxHeight: `${availableSpace}px`,
      });
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, safeOptions.length]);

  return (
    <div
      ref={rootRef}
      className={`ed-crit__select ${open ? "is-open" : ""} ${
        openUp ? "is-open-up" : ""
      } ${disabled ? "is-disabled" : ""}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className="inp ed-crit__select-trigger"
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`ed-crit__select-value ${value ? "" : "is-placeholder"}`.trim()}>
          {value || placeholder}
        </span>
      </button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className="ed-crit__select-menu is-open is-floating"
              role="listbox"
              style={menuStyle}
            >
              {safeOptions.map((opt) => {
                const selected = opt === value;
                return (
                  <button
                    key={opt}
                    type="button"
                    className={`ed-crit__select-option ${selected ? "is-selected" : ""}`.trim()}
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                    aria-selected={selected}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default function W2EditarJustificativa({
  open = false,
  onCancel = () => {},
  onConfirm = () => {},
  camposAlterados = [],
}) {
  const [causador, setCausador] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [optionsCausador, setOptionsCausador] = useState([]);
  const [loadingCausadores, setLoadingCausadores] = useState(false);
  const [erroCausadores, setErroCausadores] = useState("");

  useEffect(() => {
    let alive = true;

    if (open) {
      setCausador("");
      setJustificativa("");
      setErroCausadores("");
      setLoadingCausadores(true);

      listDesviosEditarOpcoes()
        .then((rows) => {
          if (!alive) return;
          const list = Array.from(
            new Set(
              (Array.isArray(rows) ? rows : [])
                .map((r) =>
                  typeof r === "string"
                    ? r.trim()
                    : String(r?.causador ?? "").trim()
                )
                .filter(Boolean)
            )
          );
          setOptionsCausador(list);
        })
        .catch((e) => {
          if (!alive) return;
          setOptionsCausador([]);
          setErroCausadores(
            e?.response?.data?.error || e?.message || "Erro ao carregar causadores."
          );
        })
        .finally(() => {
          if (!alive) return;
          setLoadingCausadores(false);
        });
    }

    return () => {
      alive = false;
    };
  }, [open]);

  if (!open) return null;

  const canConfirm =
    !!causador &&
    justificativa.trim().length > 0 &&
    !/^\s*$/.test(justificativa);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      causador,
      justificativa: justificativa.trim(),
    });
  };

  return createPortal(
    <div className="ed-alert__backdrop" role="dialog" aria-modal="true">
      <div className="ed-crit__card">
        <div className="ed-crit__header">
          <h3 className="ed-crit__title">Justificativa da edição</h3>
          <button
            type="button"
            className="ed-crit__close"
            aria-label="Fechar"
            onClick={onCancel}
          >
            ×
          </button>
        </div>

        <div className="ed-crit__content">
          <div className="ed-crit__box">
            <h4 className="ed-crit__subtitle">Edição da programação</h4>

            <p className="ed-crit__text">
              Você está alterando campos desta programação. Ao salvar, o sistema
              registrará o <strong>causador</strong> e a <strong>justificativa</strong>{" "}
              desta edição para consultas futuras.
            </p>

            {camposAlterados?.length > 0 && (
              <div className="ed-crit__campos">
                <span className="ed-crit__campos-label">Campos alterados:</span>
                <ul className="ed-crit__campos-list">
                  {camposAlterados.map((nome) => (
                    <li key={nome}>{nome}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="ed-crit__text ed-crit__text--hint">
              Informe abaixo o <strong>causador</strong> e a{" "}
              <strong>justificativa</strong> dessa alteração:
            </p>

            <div className="ed-crit__grid">
              <div className="ed-crit__field">
                <label className="ed-crit__label">
                  Causador <span className="ed-crit__required">*</span>
                </label>
                <CritDropdown
                  value={causador}
                  onChange={setCausador}
                  options={optionsCausador}
                  placeholder={
                    loadingCausadores
                      ? "Carregando causadores..."
                      : optionsCausador.length
                      ? "Selecione o causador..."
                      : "Sem causadores cadastrados"
                  }
                  disabled={loadingCausadores || optionsCausador.length === 0}
                />
                {!!erroCausadores && (
                  <small className="editar-helper">
                    Falha ao buscar causadores: {erroCausadores}
                  </small>
                )}
              </div>
            </div>

            <div className="ed-crit__field ed-crit__field--full">
              <label className="ed-crit__label">
                Justificativa <span className="ed-crit__required">*</span>
              </label>
              <textarea
                className="inp"
                rows={4}
                placeholder="Explique resumidamente o motivo da alteração..."
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="ed-crit__footer">
          <button
            className="editar-btn editar-btn--ghost"
            type="button"
            onClick={onCancel}
          >
            Voltar
          </button>
          <button
            className="editar-btn editar-btn--primary"
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            title={
              !canConfirm
                ? "Selecione o causador e preencha a justificativa"
                : "Salvar edição"
            }
          >
            Salvar edição
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
